import { IPoint2d } from '../math/IPoint2d';
import { Line2d } from '../math/Line2d';
import { IShape2d, IShapePoint2d } from '../types/shape';

// FNV-1a 32位算法参数
const FNV_OFFSET_BASIS = 2166136261;
const FNV_PRIME = 16777619;

/**
 * 根据字符串数组生成哈希值（速度优先）
 * 使用 FNV-1a 算法，碰撞率更低，分布更均匀
 * @param data 字符串数组
 * @returns 哈希值（数字）
 */
export function genStrsHash(data: string[]): number {
  if (data.length === 0) return 0;
  data.sort();

  let hash = FNV_OFFSET_BASIS;
  for (let i = 0; i < data.length; i++) {
    const str = data[i];
    // 对每个字符串应用 FNV-1a 算法
    for (let j = 0; j < str.length; j++) {
      hash ^= str.charCodeAt(j);
      hash = Math.imul(hash, FNV_PRIME); // 使用 Math.imul 优化32位整数乘法
    }
    // 在字符串之间添加分隔符
    hash ^= 31; // 分隔符
    hash = Math.imul(hash, FNV_PRIME);
  }
  return hash >>> 0; // 转换为无符号32位整数
}

export function genPolygonHash(mainOutline: IShapePoint2d[], holeOutlines: IShapePoint2d[][] = []) {
  const outline = [...mainOutline, ...holeOutlines.flat()];

  // 计算哈希，使用FNV-1a算法
  let hash = FNV_OFFSET_BASIS;
  for (let i = 0; i < outline.length; i++) {
    const point = outline[i];
    hash ^= point.x * FNV_PRIME;
    hash ^= point.y * FNV_PRIME;
    if (point.center) {
      hash ^= point.center.x * FNV_PRIME;
      hash ^= point.center.y * FNV_PRIME;
      hash ^= point.clockwise ? 1 : 0 * FNV_PRIME;
    }
    hash = Math.imul(hash, FNV_PRIME);
  }
  return hash >>> 0;
}

export function genLineSegHash(start: IPoint2d, end: IPoint2d, fixed = 0) {
  return genStrsHash([
    `${start.x.toFixed(fixed)}-${start.y.toFixed(fixed)}-${end.x.toFixed(fixed)}-${end.y.toFixed(fixed)}`,
    `${end.x.toFixed(fixed)}-${end.y.toFixed(fixed)}-${start.x.toFixed(fixed)}-${start.y.toFixed(fixed)}`
  ]);
}

export function genPointHash(point: IPoint2d, fixed = 0) {
  return genStrsHash([`${point.x.toFixed(fixed)}-${point.y.toFixed(fixed)}`]);
}

export function genLineHash(line: Line2d, fractionDigits = 3): string {
  let { A, B, C } = line.generalForm;

  // 1. 归一化长度，除以法向量的模长 (Math.hypot 是求平方和的平方根)
  const length = Math.hypot(A, B);
  if (length === 0) return 'invalid'; // 防止除以0
  A = A / length;
  B = B / length;
  C = C / length;
  // 2. 归一化符号，保证唯一性（比如规定第一个不为0的系数必须为正）
  // thresholdValue 通常取 1e-6
  const threshold = 1e-6;
  if (A < -threshold || (Math.abs(A) <= threshold && B < -threshold)) {
    A = -A;
    B = -B;
    C = -C;
  }

  // 防止 -0.000 的情况出现
  if (Math.abs(A) <= threshold) A = 0;
  if (Math.abs(B) <= threshold) B = 0;
  if (Math.abs(C) <= threshold) C = 0;
  // 3. 拼接带精度的字符串
  return `${A.toFixed(fractionDigits)},${B.toFixed(fractionDigits)},${C.toFixed(fractionDigits)}`;
}

export function genShapeHash(shape: IShape2d): string {
  if (shape instanceof Line2d) {
    return genLineHash(shape);
  }
  // TODO: 待补全
  return '';
}
